"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import {cn} from "@/shadcn/lib/utils"

function Dialog({
                  ...props
                }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
                         ...props
                       }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
                        ...props
                      }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
                       ...props
                     }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
                         className,
                         ...props
                       }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
      <DialogPrimitive.Overlay
          data-slot="dialog-overlay"
          className={cn(
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
              className
          )}
          {...props}
      />
  )
}

function DialogContent({
                         className,
                         children,
                         ...props
                       }: React.ComponentProps<typeof DialogPrimitive.Content>) {
  return (
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay/>
        <DialogPrimitive.Content
            data-slot="dialog-content"
            className={cn(
                "bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-slate-200 p-6 shadow-lg duration-200 sm:max-w-lg dark:bg-slate-950 dark:border-slate-800",
                className
            )}
            {...props}
        >
          {children}
          <DialogPrimitive.Close
              className="ring-offset-white focus:ring-slate-950 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
              {/*<XIcon/>*/}
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
  )
}

function DialogHeader({className, ...props}: React.ComponentProps<"div">) {
  return (
      <div
          data-slot="dialog-header"
          className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
          {...props}
      />
  )
}

function DialogFooter({className, ...props}: React.ComponentProps<"div">) {
  return (
      <div
          data-slot="dialog-footer"
          className={cn(
              "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
              className
          )}
          {...props}
      />
  )
}

function DialogTitle({
                       className,
                       ...props
                     }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
      <DialogPrimitive.Title
          data-slot="dialog-title"
          className={cn("text-lg leading-none font-semibold", className)}
          {...props}
      />
  )
}

function DialogDescription({
                             className,
                             ...props
                           }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
      <DialogPrimitive.Description
          data-slot="dialog-description"
          className={cn("text-slate-500 text-sm dark:text-slate-400", className)}
          {...props}
      />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
